import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { openaiService } from '../../../lib/openaiService';

const OpenAIAnalyticsInsights = ({ analyticsData }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await openaiService?.generateAnalyticsInsights(analyticsData);
      setInsights(result);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colorMap = {
      high: 'error',
      medium: 'warning',
      low: 'primary'
    };
    return colorMap?.[priority] || 'primary';
  };

  const getPriorityBg = (priority) => {
    const bgMap = {
      high: 'bg-error/10 text-error',
      medium: 'bg-warning/10 text-warning',
      low: 'bg-primary/10 text-primary'
    };
    return bgMap?.[priority] || 'bg-primary/10 text-primary';
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      optimization: 'Zap',
      expansion: 'TrendingUp',
      risk: 'AlertTriangle',
      opportunity: 'Target'
    };
    return iconMap?.[category] || 'Lightbulb';
  };

  return (
    <div className="card border-l-4 border-l-accent">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Sparkles" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-foreground">OpenAI Analytics</h2>
            <p className="text-sm text-muted-foreground">AI-powered business insights</p>
          </div>
        </div>
        <Button
          onClick={generateInsights}
          disabled={loading}
          size="sm"
          iconName="RefreshCw"
          iconPosition="left"
        >
          {loading ? 'Analyzing...' : 'Generate'}
        </Button>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" />
            <div>
              <p className="text-sm font-medium text-error">Error</p>
              <p className="text-xs text-error/80">{error}</p>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )}

      {!loading && !insights && !error && (
        <div className="text-center py-12">
          <Icon name="Brain" size={48} color="var(--color-accent)" className="mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">Click "Generate" to get AI-powered insights</p>
        </div>
      )}

      {!loading && insights && (
        <div className="space-y-4">
          {/* Summary */}
          {insights?.summary && (
            <div className="bg-muted/50 rounded-lg p-4 mb-4">
              <p className="text-sm text-foreground">{insights?.summary}</p>
            </div>
          )}

          {/* Insights */}
          {insights?.insights?.map((insight, index) => (
            <div
              key={index}
              className="bg-muted/50 rounded-lg p-5 hover:bg-muted transition-colors duration-250"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-card flex items-center justify-center flex-shrink-0">
                  <Icon
                    name={getCategoryIcon(insight?.category)}
                    size={20}
                    color="var(--color-accent)"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-base font-semibold text-foreground">{insight?.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityBg(insight?.priority)}`}>
                          {insight?.priority?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{insight?.description}</p>
                    </div>
                  </div>

                  {insight?.potentialImpact && (
                    <div className="bg-card rounded-lg p-3 mb-3">
                      <div className="text-xs text-muted-foreground mb-1">Potential Impact</div>
                      <div className="text-sm font-semibold text-success">{insight?.potentialImpact}</div>
                    </div>
                  )}

                  {insight?.implementationTime && (
                    <div className="bg-card rounded-lg p-3 mb-3">
                      <div className="text-xs text-muted-foreground mb-1">Implementation Time</div>
                      <div className="text-sm font-semibold text-foreground">{insight?.implementationTime}</div>
                    </div>
                  )}

                  {insight?.recommendations && insight?.recommendations?.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-foreground mb-2">Recommended Actions:</div>
                      <ul className="space-y-2">
                        {insight?.recommendations?.map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OpenAIAnalyticsInsights;